import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { changeFormField, changeFormFieldValue, removeFieldError } from 'apps/auth/actions/formAction';
import InputText from 'apps/auth/components/form-multi/InputText';
import { validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import { DROP_BOX } from 'apps/auth/constants/FieldType';
import { useExtra, useFields, useLanguage } from 'apps/auth/hooks/selectors';

import InputAutoCompleteAddressList from './InputAutoCompleteAddressList';
import { AutoAddressCompleteAddressService } from '../utils/AutoAddressCompleteAddressService';
import ModalComponent from '../utils/ModalComponent';

const InputAutoCompleteAddress = ({ formName, ...props }) => {
    const { country_list, allCountries } = useExtra();
    const { post_code: fieldPostCode, resident_country: residentCountry, city: fieldCity } = useFields(formName);
    const { lang } = useLanguage();

    const countryCode = useMemo(() => {
        let country = props.countryCode;
        if (residentCountry?.type === DROP_BOX && residentCountry?.value) {
            return residentCountry.value.toLowerCase();
        }
        if (!!country_list && !!country_list.length && residentCountry?.value) {
            country = country_list.find((country) => country.id.toString() === residentCountry.value.toString());
        } else if (!!allCountries && residentCountry?.value) {
            //Ukraine and Belarus case
            country = allCountries[residentCountry.value.toString()];
        }
        return country?.code ?? props.countryCode;
    }, [country_list, allCountries, residentCountry, props.countryCode]);

    const [items, setItems] = useState([]);
    const [isOpenedDropbox, toggleDropbox] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState({
        address: '',
        id: '',
    });
    const controllerRef = useRef(null);
    const { current: getAddressService } = useRef(new AutoAddressCompleteAddressService(formName, lang, props.setItems, props.selectItem, controllerRef));
    const autoFillFieldsRef = useRef({ city: fieldCity, postCode: fieldPostCode });

    autoFillFieldsRef.current = { city: fieldCity, postCode: fieldPostCode };

    const setSelectedAddressFunc = (address, id = '') => setSelectedAddress({ address, id });

    const openDropbox = () => toggleDropbox(true);
    const closeDropbox = () => toggleDropbox(false);

    const findCurrentItem = (items, value) => {
        if (items.length > 0) {
            const currentItemArray = items.filter((item) => getAddressService.serializeItem(item).mainText === value);
            if (currentItemArray.length > 0) {
                return getAddressService.serializeItem(currentItemArray[0]).mainText;
            }
        }
        return '';
    };

    const [selectItem] = useMemo(() => {
        const validateAutoFilledFields = ({ city, postCode }) => {
            const { city: currentCityField, postCode: currentPostCodeField } = autoFillFieldsRef.current;
            if (typeof city !== 'undefined' && currentCityField) {
                validationAndUpdateValue(formName, city, currentCityField, props.changeFormField);
            }
            if (typeof postCode !== 'undefined' && currentPostCodeField) {
                validationAndUpdateValue(formName, postCode, currentPostCodeField, props.changeFormField);
            }
        };
        getAddressService.setCountryCode(countryCode);
        getAddressService.setFields(fieldCity, fieldPostCode, props.field);
        getAddressService.setReduxAction(props.changeFormFieldValue, props.changeFormField);

        return getAddressService.selectPlace(setSelectedAddressFunc, closeDropbox, validateAutoFilledFields);
    }, [countryCode]);

    const selectInputClient = (e, value) => {
        const isCurrentValueEqualItem = value.trim() === findCurrentItem(items, value.trim());
        if (!isCurrentValueEqualItem) {
            if (fieldCity.value || fieldCity.isError) {
                const newField = { ...fieldCity, value: '', isError: false };
                props.changeFormField(formName, newField);
            }
            if (fieldPostCode.value || fieldPostCode.isError) {
                const newField = { ...fieldPostCode, value: '', isError: false };
                props.changeFormField(formName, newField);
            }
        }

        validationAndUpdateValue(formName, value, props.field, props.changeFormField).then(closeDropbox);
    };

    return (
        <>
            {!isOpenedDropbox ? (
                <InputText
                    onClick={openDropbox}
                    field={props.field}
                    formName={formName}
                    autoFocus={props.focus}
                    onFocus={openDropbox}
                    onChange={props.onChange}
                    isClearErrorOnFocus={true}
                    validateOnBlur={false}
                    onBlur={props.onBlur}
                    isNewInput
                    isReadOnly={props.disabledAddressInput}
                    alwaysHasHint={props.alwaysHasHint}
                />
            ) : (
                <ModalComponent canCloseModal={false}>
                    <InputAutoCompleteAddressList
                        selectItem={selectItem}
                        selectInputClient={selectInputClient}
                        items={items}
                        setItems={setItems}
                        field={props.field}
                        countryCode={countryCode}
                        selectedAddress={selectedAddress}
                        setSelectedAddress={setSelectedAddressFunc}
                        searchPlaceholder={props.searchPlaceholder}
                        searchDropBoxEmpty={props.searchDropBoxEmpty}
                        formName={formName}
                        onCloseButtonClick={closeDropbox}
                        removeFieldError={props.removeFieldError}
                    />
                </ModalComponent>
            )}
        </>
    );
};

InputAutoCompleteAddress.propTypes = {
    countryCode: PropTypes.string,
    formName: PropTypes.string.isRequired,
    field: PropTypes.object.isRequired,
    disabledAddressInput: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ messages }) => ({
    searchPlaceholder: messages.jsMessages['site.field.search.placeholder'],
    searchDropBoxEmpty: messages.jsMessages['site.search.dropdox.empty'],
});

export default connect(mapStateToProps, {
    changeFormField,
    changeFormFieldValue,
    removeFieldError,
})(InputAutoCompleteAddress);
